import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EntityManager } from '@dch/entity-manager';
import { of } from 'rxjs';
import { catchError, withLatestFrom, mergeMap } from 'rxjs/operators';
import { EnvironmentService, TranslationService } from '@dch/ng-common';

import {
  PAYMENT_DATA_ENTITY,
  UPDATE_BILLING_PAYMENT_ENTITY,
} from 'src/app/my-bill/config/my-bill-entity.config';
import {
  paymentService,
  IPaymentDataResponse,
} from '@dch/billing-business-logic';

import { getBillingBalanceRequest } from '../actions/billing-balance.actions';
import { getBillingTransactionsRequest } from '../actions/billing-transactions.actions';

import {
  savePaymentDataFailure,
  savePaymentDataRequest,
  savePaymentDataSuccess,
  updateBillingPaymentFailure,
  updateBillingPaymentRequest,
  updateBillingPaymentSuccess,
} from '../actions/payment.actions';
import { ApplicationState } from '../reducers';
import { selectCustomer } from '../selectors/customer-account.selector';
import { RemoteLoggerService } from 'src/app/my-bill/services/remote-logger.service';

@Injectable()
export class PaymentDataEffects {
  savePaymentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePaymentDataRequest),
      withLatestFrom(this.store.select(selectCustomer)),
      mergeMap(([{ billingAccountNumber, requestBody }, customer]) => {
        const parameters = {
          billingAccountNumber,
          cdbId: customer.customerId,
        };
        return EntityManager.saveEntity<IPaymentDataResponse>({
          ...EntityManager.createEntity(PAYMENT_DATA_ENTITY, parameters),
          value: requestBody,
        }).pipe(
          mergeMap((savedEntity) => {
            const success = Boolean(savedEntity.value);
            const returnedAction = success
              ? savePaymentDataSuccess({
                  response: savedEntity.value,
                })
              : savePaymentDataFailure();
            if (success) {
              const ogoneUrl = this.environmentService.isProd
                ? this.translationService.getTranslatedValueFor(
                    'bills.payment.ogoneLiveTargetEndpointUrl'
                  )
                : this.translationService.getTranslatedValueFor(
                    'bills.payment.ogoneTestTargetEndpointUrl'
                  );
              const ogoneHtml = paymentService.getOgoneTemplate(
                savedEntity.value,
                ogoneUrl
              );
              const parser = new DOMParser();
              const localDocument = parser.parseFromString(
                ogoneHtml,
                'text/html'
              );
              const form = localDocument.body.firstChild as HTMLFormElement;
              document.body.appendChild(form);
              form.submit();
              document.body.removeChild(form);
            } else {
              this.remoteLoggerService.logErrorImmediately(
                'Payment data call success - but no value in the response'
              );
            }
            return of(returnedAction);
          }),
          catchError((error) => {
            this.remoteLoggerService.logErrorImmediately(
              'Payment data call failed - Error during the creation of Ogone parameters',
              error
            );
            return of(savePaymentDataFailure());
          })
        );
      })
    )
  );

  updateBillingPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBillingPaymentRequest),
      withLatestFrom(this.store.select(selectCustomer)),
      mergeMap(([{ billingAccountNumber, requestBody }, customer]) => {
        const parameters = {
          billingAccountNumber,
          cdbId: customer.customerId,
        };
        return EntityManager.saveEntity<IPaymentDataResponse>({
          ...EntityManager.createEntity(
            UPDATE_BILLING_PAYMENT_ENTITY,
            parameters
          ),
          value: requestBody,
        }).pipe(
          mergeMap((updateEntity) => {
            const outActions = [];
            if (updateEntity.value) {
              outActions.push(
                updateBillingPaymentSuccess({
                  response: updateEntity.value,
                })
              );
              if (paymentService.isPaymentSuccessful(requestBody.status)) {
                outActions.push(getBillingBalanceRequest());
                if (billingAccountNumber) {
                  outActions.push(
                    getBillingTransactionsRequest(billingAccountNumber)
                  );
                }
              }
            } else {
              outActions.push(updateBillingPaymentFailure());
            }
            return outActions;
          }),
          catchError((error) => {
            this.remoteLoggerService.logErrorImmediately(
              'update payment call failed',
              error
            );
            return of(updateBillingPaymentFailure());
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private environmentService: EnvironmentService,
    private remoteLoggerService: RemoteLoggerService
  ) {}
}
